<template>
  <div style="height:100%">
    <!-- 无token时直接显示登录页 -->
    <router-view v-if="!hasToken" />

    <!-- 有token时显示主布局 -->
    <el-container v-else class="app-container">
    <!-- 左侧菜单 -->
    <el-aside width="220px" class="app-aside">
      <div class="logo">
        <el-icon :size="24"><FolderOpened /></el-icon>
        <span>收单管理</span>
      </div>
      <el-menu
        :default-active="activeMenu"
        router
        class="app-menu"
        background-color="#1d1e1f"
        text-color="#bfcbd9"
        active-text-color="#409eff"
      >
        <template v-for="menu in menus" :key="menu.id">
          <!-- 目录：有children -->
          <el-sub-menu v-if="menu.children && menu.children.length" :index="String(menu.id)">
            <template #title>
              <el-icon v-if="menu.icon"><component :is="menu.icon" /></el-icon>
              <span>{{ menu.menuName }}</span>
            </template>
            <el-menu-item
              v-for="child in menu.children"
              :key="child.id"
              :index="child.path"
            >
              <el-icon v-if="child.icon"><component :is="child.icon" /></el-icon>
              <span>{{ child.menuName }}</span>
            </el-menu-item>
          </el-sub-menu>
          <!-- 菜单：无children -->
          <el-menu-item v-else :index="menu.path">
            <el-icon v-if="menu.icon"><component :is="menu.icon" /></el-icon>
            <span>{{ menu.menuName }}</span>
          </el-menu-item>
        </template>
      </el-menu>
    </el-aside>

    <!-- 右侧区域 -->
    <el-container>
      <!-- 顶部栏 -->
      <el-header class="app-header">
        <div class="header-left">
          <span class="header-title">收单管理系统</span>
        </div>
        <div class="header-right">
          <!-- 刷新当前页 -->
          <el-icon class="refresh-icon" @click="refreshPage" :size="18" title="刷新当前页">
            <Refresh />
          </el-icon>
          <!-- 消息通知 -->
          <MessageNotification @refresh-requested="handleAutoRefresh" />
          
          <el-dropdown @command="handleCommand">
            <span class="user-info">
              <el-icon style="margin-right: 4px;"><UserFilled /></el-icon>
              {{ currentUser?.username || currentUser?.account || '用户' }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="changePassword">
                  <el-icon><Lock /></el-icon>修改密码
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <el-icon><SwitchButton /></el-icon>退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 主内容区 -->
      <el-main class="app-main">
        <router-view :key="routerViewKey" />
      </el-main>
    </el-container>

    <!-- 修改密码弹窗 -->
    <el-dialog v-model="pwdDialogVisible" title="修改密码" width="400px" :close-on-click-modal="false">
      <el-form ref="pwdFormRef" :model="pwdForm" :rules="pwdRules" label-width="80px">
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" placeholder="请输入新密码" show-password />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" placeholder="请确认新密码" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="pwdDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="pwdLoading" @click="handleChangePassword">确定</el-button>
      </template>
    </el-dialog>
  </el-container>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getToken, getUser, getMenus, removeToken } from './utils/auth'
import { logout, updateUser } from './api'
import { emitRefresh } from './utils/eventBus'
import MessageNotification from './components/MessageNotification.vue'

// 刷新当前页（只重载框架内组件，不刷新浏览器）
const routerViewKey = ref(0)
const lastRefreshTime = ref(0)
const refreshPage = () => {
  routerViewKey.value++
}

// SSE新消息触发的自动刷新（7秒节流，避免短时间内多次刷新）
// 优先精确刷新注册了监听的页面，未注册则 fallback 到整页重建
function handleSseRefresh(routeName) {
  const now = Date.now()
  if (now - lastRefreshTime.value > 7000) {
    lastRefreshTime.value = now
    if (routeName && emitRefresh(routeName)) return  // 精确刷新成功
    routerViewKey.value++  // fallback 整页重建
  }
}

const handleAutoRefresh = (routeName) => {
  handleSseRefresh(routeName)
}

const route = useRoute()
const router = useRouter()

// 用ref实现响应式token状态
const tokenState = ref(getToken())
const hasToken = computed(() => !!tokenState.value)
const currentUser = computed(() => getUser())
const menus = computed(() => {
  const list = getMenus()
  return list.filter(m => m.menuType !== 3) // 过滤掉按钮类型
})

const activeMenu = computed(() => {
  if (route.path.startsWith('/batch')) return '/batch-list'
  if (route.path.startsWith('/excel')) return '/excel-file-list'
  return route.path
})

// 修改密码
const pwdDialogVisible = ref(false)
const pwdLoading = ref(false)
const pwdFormRef = ref(null)
const pwdForm = reactive({
  newPassword: '',
  confirmPassword: ''
})

const validateConfirmPassword = (rule, value, callback) => {
  if (value !== pwdForm.newPassword) {
    callback(new Error('两次输入的密码不一致'))
  } else {
    callback()
  }
}

const pwdRules = {
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码长度不少于6位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请确认新密码', trigger: 'blur' },
    { validator: validateConfirmPassword, trigger: 'blur' }
  ]
}

const handleCommand = (command) => {
  if (command === 'logout') {
    ElMessageBox.confirm('确定要退出登录吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      try {
        await logout()
      } catch (e) {
        // 忽略登出接口错误
      }
      removeToken()
      tokenState.value = null
      router.push('/login')
    }).catch(() => {})
  } else if (command === 'changePassword') {
    pwdForm.newPassword = ''
    pwdForm.confirmPassword = ''
    pwdDialogVisible.value = true
  }
}

const handleChangePassword = async () => {
  const valid = await pwdFormRef.value.validate().catch(() => false)
  if (!valid) return

  pwdLoading.value = true
  try {
    await updateUser(currentUser.value.userId, { password: pwdForm.newPassword })
    ElMessage.success('密码修改成功，请重新登录')
    pwdDialogVisible.value = false
    removeToken()
    tokenState.value = null
    router.push('/login')
  } catch (e) {
    // 错误已在拦截器中提示
  } finally {
    pwdLoading.value = false
  }
}
</script>

<style>
html, body, #app { margin: 0; padding: 0; height: 100%; }
.app-container { height: 100vh; }
.app-aside {
  background: #1d1e1f;
  overflow-y: auto;
}
.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 18px 16px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #333;
}
.app-menu {
  border-right: none;
}
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
  padding: 0 20px;
  height: 56px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.header-right {
  display: flex;
  align-items: center;
}
.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #606266;
  font-size: 14px;
}
.user-info:hover {
  color: #409eff;
}
.refresh-icon {
  cursor: pointer;
  color: #606266;
  margin-right: 16px;
  transition: transform 0.3s, color 0.3s;
}
.refresh-icon:hover {
  color: #409eff;
  transform: rotate(90deg);
}
.app-main {
  background: #f0f2f5;
  padding: 20px;
  overflow-y: auto;
}
</style>
